import { Injectable } from '@angular/core';
import { UserProfileService } from './user-profile.service';

/**
 * Permission keys known to the UI (UHD-1716). They must match the keys checked by
 * perform-api (services/permissions.services.ts) and seeded in dbo.Permissions.
 */
export enum Permission {
  DeviceRemoteDebug = 'device.remote_debug',
  DeviceLogsViewer = 'device.logs_viewer',
  /** Users tab: Roles column, Role filter and Extra roles field (UHD-1737) */
  UsersAssignRoles = 'users.assign_roles',
  /** Roles tab */
  UsersManageRoles = 'users.manage_roles',
  /** Permissions tab (UHD-1737) */
  UsersManagePermissions = 'users.manage_permissions'
}

/**
 * Per-user permission checks on top of the loaded user profile. Everything gated by a
 * permission is hidden by default: a missing or malformed permissions list means "no".
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  constructor(private readonly userProfileService: UserProfileService) {}

  /** Effective permission keys of the signed-in user ([] until the profile is loaded). */
  get permissions(): string[] {
    const list = this.userProfileService.userProfile?.permissions;
    return Array.isArray(list) ? list : [];
  }

  /** Effective role names of the signed-in user ([] until the profile is loaded). */
  get roles(): string[] {
    const list = this.userProfileService.userProfile?.roles;
    return Array.isArray(list) ? list : [];
  }

  has(permission: Permission | string): boolean {
    return this.permissions.includes(permission);
  }

  hasAny(permissions: (Permission | string)[]): boolean {
    return permissions.some((p) => this.has(p));
  }

  hasRole(role: string): boolean {
    return this.roles.includes(role);
  }
}
